import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import client, { apiError, API, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, Pill, Loading, Crest } from "../components/common";

const box = "border border-pslborder rounded-sm bg-white p-4";
const label = "text-[11px] uppercase tracking-wider text-green font-bold";
const btnG = "px-3 py-1.5 bg-green text-cream text-sm font-bold rounded-sm hover:bg-ink transition-colors";
const inp = "border border-pslborder rounded-sm p-2 text-sm focus:outline-none focus:border-gold";

export default function AdminPage() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user || user.role === "fan") return <Navigate to="/" />;
  const r = user.role;
  return (
    <div data-testid="admin-page">
      <div className="bg-green text-cream rounded-sm p-6 mb-6">
        <div className="text-xs uppercase tracking-widest text-gold">Staff console</div>
        <h1 className="font-head font-black text-2xl">{user.display_name || user.username}</h1>
        <Pill tone="gold">{r}{user.desk ? ` · ${user.desk}` : ""}{user.section ? ` · ${user.section}` : ""}{user.club_slug ? ` · ${user.club_slug}` : ""}</Pill>
      </div>
      {(r === "super_master_admin" || r === "super_admin") && <SuperPanel role={r} />}
      {r === "psl_staffer" && <StafferPanel />}
      {r === "media" && <MediaPanel user={user} />}
      {r === "sales" && <SalesPanel user={user} />}
      {r === "club_official" && <ClubPanel user={user} />}
    </div>
  );
}

/* ---------------- Super Admin ---------------- */
function SuperPanel({ role }) {
  const [tab, setTab] = useState("staff");
  const tabs = [["staff", "Staff & roles"], ["fans", "Fans & supporters"], ["moderation", "Moderation"], ["table", "League table"], ["reports", "Reports"], ["scoring", "Fantasy rules"], ["editorial", "Editorial"], ["audit", "Audit log"], ["zig", "ZiG book"], ["slots", "Ad slots"], ["feedback", "Feedback"], ["conflict", "Conflict log"]];
  return (
    <div>
      <TabBar tabs={tabs} tab={tab} setTab={setTab} />
      {tab === "staff" && <UsersPanel role={role} />}
      {tab === "fans" && <FansPanel role={role} />}
      {tab === "moderation" && <ModerationPanel />}
      {tab === "table" && <TableEditorPanel />}
      {tab === "reports" && <ReportsPanel />}
      {tab === "scoring" && <ScoringPanel />}
      {tab === "editorial" && <EditorialPanel canRevoke />}
      {tab === "audit" && <AuditPanel />}
      {tab === "zig" && <ZigPanel canRebase={role === "super_master_admin"} />}
      {tab === "slots" && <SalesPanel superView />}
      {tab === "feedback" && <FeedbackPanel />}
      {tab === "conflict" && <ConflictPanel />}
    </div>
  );
}

function UsersPanel({ role }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = () => client.get(`/admin/users?group=staff&q=${q}`).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const suspend = async (u) => { try { await client.post(`/admin/users/${u.id}/suspend`); load(); toast.success("Updated"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async (u) => {
    if (!window.confirm(`Permanently delete ${u.display_name || u.username} (${u.role})? Removes their content. Cannot be undone.`)) return;
    try { await client.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const exportPii = async () => { try { const { data } = await client.get("/admin/users/export/pii"); toast.success(`Exported ${data.length} records`); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const saveProfile = async () => {
    try {
      const payload = { email: edit.email, authenticated: edit.authenticated };
      if (edit.display_name !== undefined) payload.display_name = edit.display_name;
      if (edit.new_password) payload.new_password = edit.new_password;
      await client.put(`/admin/users/${edit.id}/profile`, payload);
      toast.success("Profile updated"); setEdit(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveRole = async () => {
    try { await client.put(`/admin/users/${edit.id}/role`, { role: edit.role }); toast.success("Role updated"); setEdit(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className={box}>
      <div className="flex gap-2 mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search username" className={inp + " flex-1"} data-testid="user-search" />
        <button onClick={load} className={btnG}>Search</button>
        {role === "super_master_admin" && <button onClick={exportPii} className="px-3 py-1.5 bg-gold text-ink text-sm font-bold rounded-sm" data-testid="export-pii-btn">Export PII</button>}
        {role === "super_master_admin" && <a href={`${API}/admin/accounts.csv`} className="px-3 py-1.5 bg-green text-cream text-sm font-bold rounded-sm" data-testid="download-accounts">Team logins CSV</a>}
        {role === "super_master_admin" && <a href={`${API}/admin/launch-pack.md`} className="px-3 py-1.5 bg-gold text-ink text-sm font-bold rounded-sm" data-testid="download-launchpack">Launch pack</a>}
        <a href={`${API}/admin/media-assets.csv`} className="px-3 py-1.5 bg-white border border-pslborder text-sm font-bold rounded-sm" data-testid="download-media-assets">Media assets CSV</a>
      </div>
      {edit && (
        <div className="mb-3 p-3 rounded-sm border border-gold bg-gold/5" data-testid="profile-edit">
          <div className={label + " mb-2"}>Authenticate & set real email · {edit.username}</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input type="email" value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} placeholder="real@premierleaguezw.com" className={inp + " flex-1 min-w-[220px]"} data-testid="edit-email" />
            <label className="flex items-center gap-1 text-sm font-semibold"><input type="checkbox" checked={!!edit.authenticated} onChange={(e) => setEdit({ ...edit, authenticated: e.target.checked })} data-testid="edit-authenticated" /> Authenticated</label>
            <button onClick={saveProfile} className={btnG} data-testid="save-profile">Save</button>
            <button onClick={() => setEdit(null)} className="text-xs text-ink/50 font-bold">Cancel</button>
          </div>
          <div className="flex flex-wrap gap-2 items-center mt-2">
            <input value={edit.display_name || ""} onChange={(e) => setEdit({ ...edit, display_name: e.target.value })} placeholder="Display name (e.g. Tendai Moyo)" className={inp + " flex-1 min-w-[220px]"} data-testid="edit-display-name" />
            <input value={edit.new_password || ""} onChange={(e) => setEdit({ ...edit, new_password: e.target.value })} placeholder="Set new password (min 8 chars, optional)" className={inp + " flex-1 min-w-[220px]"} data-testid="edit-password" />
          </div>
          {role === "super_master_admin" && (
            <div className="mt-3 pt-3 border-t border-gold/30 flex flex-wrap gap-2 items-center" data-testid="role-editor">
              <span className={label}>Appoint / revoke role</span>
              <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} className={inp} data-testid="edit-role">
                {["fan", "club_official", "sales", "media", "psl_staffer", "super_admin", "super_master_admin"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button onClick={saveRole} className={btnG} data-testid="save-role">Update role</button>
              <span className="text-xs text-ink/40">Only the Super Master Admin (Julian) can change roles.</span>
            </div>
          )}
          <p className="text-xs text-ink/40 mt-1">Password stays unchanged. Real emails are swapped in only after Super Admin authentication.</p>
        </div>
      )}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-xs uppercase text-ink/50 border-b border-pslborder"><th className="py-2">Username</th><th>Role</th><th>Email</th><th>Phone</th><th>Auth</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-pslborder" data-testid={`user-row-${u.username}`}>
                <td className="py-2 font-semibold">{u.username}{u.suspended && <Pill tone="live">suspended</Pill>}</td>
                <td className="text-xs">{u.role}</td>
                <td className="text-xs" data-testid={`user-email-${u.username}`}>{u.email || <span className="text-ink/30">hidden</span>}</td>
                <td className="text-xs">{u.phone || <span className="text-ink/30">hidden</span>}</td>
                <td>{u.authenticated ? <Pill tone="green">yes</Pill> : <span className="text-ink/30 text-xs">no</span>}</td>
                <td className="whitespace-nowrap">
                  {u.role !== "fan" && <button onClick={() => setEdit({ id: u.id, username: u.username, email: u.email || "", authenticated: !!u.authenticated, role: u.role, display_name: u.display_name || "", new_password: "" })} className="text-xs text-green font-bold mr-3" data-testid={`edit-${u.username}`}>Edit</button>}
                  {u.role !== "super_master_admin" && <button onClick={() => suspend(u)} className="text-xs text-live font-bold">{u.suspended ? "Unsuspend" : "Suspend"}</button>}
                  {u.role !== "super_master_admin" && <button onClick={() => del(u)} className="text-xs text-ink/60 font-bold hover:text-live ml-3" data-testid={`del-${u.username}`}>Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink/40 mt-2">Internal staff accounts only — roles that work on the site. Fans & supporters are in their own tab. Email/phone visible to Super Admins & Master; use Edit to authenticate a profile and swap in its real email.</p>
    </div>
  );
}

/* ---------------- Fans & supporters (audience analytics) ---------------- */
function FanStat({ label: l, value }) {
  return (
    <div className={box}>
      <div className="text-[11px] uppercase tracking-wider text-ink/50">{l}</div>
      <div className="font-head font-black text-2xl text-green tabular">{value}</div>
    </div>
  );
}

function FansPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const load = () => client.get(`/admin/fans?q=${encodeURIComponent(q)}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading label="Loading supporters" />;
  const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"; } catch { return "—"; } };
  const suspend = async (f) => {
    let reason = null;
    if (!f.suspended) { reason = window.prompt("Reason for suspending (e.g. abusive language, spam):", "Abusive language"); if (reason === null) return; }
    try { await client.post(`/admin/users/${f.id}/suspend`, { reason }); toast.success(f.suspended ? "Unsuspended" : "Suspended"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (f) => {
    if (!window.confirm(`Permanently delete ${f.display_name || f.username}? Removes their posts and fantasy team. Cannot be undone.`)) return;
    try { await client.delete(`/admin/users/${f.id}`); toast.success("Supporter deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="fans-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <FanStat label="Total supporters" value={data.summary.total} />
        <FanStat label="New (7 days)" value={data.summary.new_7d} />
        <FanStat label="With fantasy team" value={data.summary.with_squad} />
        <FanStat label="Active (posted)" value={data.summary.active} />
      </div>
      <div className={box}>
        <div className="flex gap-2 mb-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name / email" className={inp + " flex-1"} data-testid="fans-search" />
          <button onClick={load} className={btnG}>Search</button>
          <a href={`${API}/admin/fans.csv`} className="px-3 py-1.5 bg-gold text-ink text-sm font-bold rounded-sm" data-testid="fans-export-csv">Export CSV</a>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-left text-xs uppercase text-ink/50 border-b border-pslborder">
              <th className="py-2">Supporter</th><th>Email</th><th>Phone</th><th>Signed up</th><th>Last login</th><th>IP</th><th>Fantasy</th><th>Posts</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {data.fans.map((f) => (
                <tr key={f.id} className="border-b border-pslborder" data-testid={`fan-row-${f.username}`}>
                  <td className="py-2 font-semibold">{f.display_name || f.username}{f.suspended && <Pill tone="live">suspended</Pill>}<div className="text-[11px] text-ink/40">@{f.username}</div></td>
                  <td className="text-xs" data-testid={`fan-email-${f.username}`}>{f.email || <span className="text-ink/30">—</span>}</td>
                  <td className="text-xs">{f.phone || <span className="text-ink/30">—</span>}</td>
                  <td className="text-xs whitespace-nowrap">{fmt(f.created_at)}</td>
                  <td className="text-xs whitespace-nowrap">{f.last_login_at ? fmt(f.last_login_at) : <span className="text-ink/30">never</span>}</td>
                  <td className="text-xs">{f.last_login_ip || <span className="text-ink/30">—</span>}</td>
                  <td className="text-xs">{f.has_squad ? <span className="text-green font-bold tabular">{f.fantasy_points ?? 0} pts</span> : <span className="text-ink/30">no team</span>}</td>
                  <td className="text-xs tabular">{(f.forum_posts || 0) + (f.timeline_posts || 0)}</td>
                  <td className="whitespace-nowrap">
                    <button onClick={() => suspend(f)} className="text-xs text-live font-bold mr-3" data-testid={`fan-suspend-${f.username}`}>{f.suspended ? "Unsuspend" : "Suspend"}</button>
                    <button onClick={() => del(f)} className="text-xs text-ink/60 font-bold hover:text-live" data-testid={`fan-delete-${f.username}`}>Delete</button>
                  </td>
                </tr>
              ))}
              {data.fans.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-ink/40">No supporters have signed up yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-ink/40 mt-2">Supporter data (email, phone, IP) is personal information — visible to Super Admins only. Use for engagement analysis; never share externally.</p>
      </div>
    </div>
  );
}

function AuditPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { client.get("/admin/audit").then((r) => setRows(r.data)); }, []);
  return (
    <div className={box}>
      <div className={label + " mb-2"}>Append-only audit log</div>
      <div className="divide-y divide-pslborder max-h-[500px] overflow-y-auto scrollbar-thin">
        {rows.map((a) => <div key={a.id} className="py-2 text-sm flex gap-3"><span className="text-ink/40 text-xs w-36 shrink-0">{new Date(a.ts).toLocaleString("en-GB")}</span><span className="font-bold text-green">{a.actor}</span><span className="text-ink/70">{a.action}</span><span className="text-ink/50 truncate">{a.detail}</span></div>)}
      </div>
    </div>
  );
}

function ZigPanel({ canRebase }) {
  const [book, setBook] = useState(null);
  const [form, setForm] = useState({ budget: 100, note: "" });
  useEffect(() => { client.get("/admin/zig-book").then((r) => { setBook(r.data); setForm({ budget: r.data.budget, note: "" }); }); }, []);
  if (!book) return <div className={box}>Loading…</div>;
  const rebase = async () => { try { await client.post("/admin/zig-book/rebase", form); toast.success("ZiG book rebased"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className={box}>
      <div className={label}>ZiG valuation book</div>
      <p className="text-sm mt-2">Budget: <b className="tabular">ZiG {book.budget}m</b> · Tick: ZiG {book.tick}m</p>
      <p className="text-xs text-ink/50 mt-1">Last rebased {new Date(book.last_rebased).toLocaleString("en-GB")} — {book.note}</p>
      {canRebase ? (
        <div className="mt-4 flex flex-wrap gap-2 items-end border-t border-pslborder pt-4">
          <div><div className="text-xs text-ink/50">New budget (ZiG m)</div><input type="number" step="0.1" value={form.budget} onChange={(e) => setForm({ ...form, budget: parseFloat(e.target.value) })} className={inp} /></div>
          <div className="flex-1 min-w-[200px]"><div className="text-xs text-ink/50">Public note</div><input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inp + " w-full"} placeholder="Monday FX rebase note" /></div>
          <button onClick={rebase} className={btnG} data-testid="rebase-btn">Rebase book</button>
        </div>
      ) : <p className="text-xs text-ink/40 mt-3">Only the Super Master Admin can rebase the book.</p>}
    </div>
  );
}

/* ---------------- Moderation (abuse reports & disputes) ---------------- */
function ModerationPanel() {
  const [rows, setRows] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [notes, setNotes] = useState({});
  const load = () => client.get(`/admin/moderation${showResolved ? "?status=all" : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [showResolved]);
  if (!rows) return <Loading label="Loading reports" />;
  const act = async (r, action) => {
    if ((action === "delete_author" || action === "delete_content") && !window.confirm("Confirm — this cannot be undone.")) return;
    try { await client.post(`/admin/moderation/${r.id}/act`, { action, note: notes[r.id] || "" }); toast.success("Action taken"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const TYPE = { timeline: "Timeline post", forum_thread: "Forum thread", forum_reply: "Forum reply", user: "User" };
  return (
    <div className="space-y-3" data-testid="moderation-panel">
      <div className="flex items-center justify-between">
        <div className={label}>Abuse reports & disputes to adjudicate</div>
        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} data-testid="mod-show-resolved" /> Show resolved</label>
      </div>
      {rows.length === 0 && <div className={box + " text-sm text-ink/50"}>Nothing to adjudicate right now.</div>}
      {rows.map((r) => (
        <div key={r.id} className={box} data-testid={`report-${r.id}`}>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Pill tone="muted">{TYPE[r.target_type] || r.target_type}</Pill>
            {r.status === "resolved" ? <Pill tone="green">resolved · {r.decision}</Pill> : <Pill tone="live">open</Pill>}
            <span className="text-xs text-ink/40 ml-auto">{new Date(r.created_at).toLocaleString("en-GB")}</span>
          </div>
          <p className="text-sm text-ink/90 border-l-2 border-gold pl-2 my-2">{r.snippet || <span className="text-ink/40">(content removed)</span>}</p>
          <div className="text-xs text-ink/60">Author: <b>{r.target_author || "—"}</b> · Reported by: <b>{r.reporter}</b>{r.reason ? <> · Reason: "{r.reason}"</> : null}</div>
          {r.status === "resolved" ? (
            <div className="text-xs text-ink/50 mt-2">Adjudicated by {r.decided_by}{r.decision_note ? ` — "${r.decision_note}"` : ""}</div>
          ) : (
            <div className="mt-3 space-y-2">
              <input value={notes[r.id] || ""} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })} placeholder="Adjudication note (optional)…" className={inp + " w-full"} data-testid={`report-note-${r.id}`} />
              <div className="flex flex-wrap gap-2">
                <button onClick={() => act(r, "dismiss")} className="text-xs font-bold px-2.5 py-1 border border-pslborder rounded-sm" data-testid={`report-dismiss-${r.id}`}>Dismiss</button>
                {r.target_type !== "user" && <button onClick={() => act(r, "delete_content")} className="text-xs font-bold px-2.5 py-1 bg-ink text-cream rounded-sm" data-testid={`report-del-content-${r.id}`}>Delete content</button>}
                <button onClick={() => act(r, "suspend_author")} className="text-xs font-bold px-2.5 py-1 bg-gold text-ink rounded-sm" data-testid={`report-suspend-${r.id}`}>Suspend author</button>
                <button onClick={() => act(r, "delete_author")} className="text-xs font-bold px-2.5 py-1 bg-live text-cream rounded-sm" data-testid={`report-del-author-${r.id}`}>Delete author</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------------- League table editor (manual standings) ---------------- */
function TableEditorPanel() {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => client.get("/table").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  if (!rows) return <Loading label="Loading table" />;
  const upd = (id, field, val) => setRows(rows.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r) => ({ id: r.id, played: +r.played || 0, won: +r.won || 0, drawn: +r.drawn || 0, lost: +r.lost || 0, gf: +r.gf || 0, ga: +r.ga || 0, points: +r.points || 0 }));
      await client.post("/staff/table", { rows: payload });
      toast.success("Table updated & re-ranked — live on the site now");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const num = "w-14 border border-pslborder rounded-sm p-1 text-sm text-center focus:outline-none focus:border-gold";
  const cols = [["played", "P"], ["won", "W"], ["drawn", "D"], ["lost", "L"], ["gf", "GF"], ["ga", "GA"], ["points", "Pts"]];
  return (
    <div className={box} data-testid="table-editor">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className={label}>Edit official standings</div>
        <button onClick={save} disabled={saving} className={btnG} data-testid="table-save">{saving ? "Saving…" : "Save & re-rank"}</button>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-xs uppercase text-ink/50 border-b border-pslborder">
            <th className="py-2 w-8">#</th><th>Club</th>{cols.map(([f, l]) => <th key={f} className="px-1 text-center">{l}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-pslborder" data-testid={`edit-row-${r.club_slug}`}>
                <td className="py-1 text-center font-head font-bold text-ink/50">{r.position}</td>
                <td className="py-1 font-semibold">{r.club_name}</td>
                {cols.map(([f]) => (
                  <td key={f} className="px-1 py-1 text-center">
                    <input type="number" className={num} value={r[f] ?? 0} onChange={(e) => upd(r.id, f, e.target.value)} data-testid={`edit-${f}-${r.club_slug}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink/40 mt-2">Enter each club's played / won / drawn / lost / goals / points. Goal difference and league positions are recalculated automatically on save and appear live on the site immediately. (Auto-sync from Sofascore is blocked for servers, so the desk maintains the table here.)</p>
    </div>
  );
}

function ConflictPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { client.get("/sales/conflict-log").then((r) => setRows(r.data)); }, []);
  return <div className={box}><div className={label + " mb-2"}>Category conflict log</div>{rows.length === 0 ? <p className="text-sm text-ink/50">No conflicts logged.</p> : rows.map((c) => <div key={c.id} className="py-2 text-sm border-b border-pslborder">{c.actor} tried <b>{c.category}</b> on {c.slot_id} — refused</div>)}</div>;
}

/* ---------------- PSL Staffer ---------------- */
function StafferPanel() {
  const [fixtures, setFixtures] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [strip, setStrip] = useState("Castle Lager Premier Soccer League");
  useEffect(() => {
    client.get("/fixtures").then((r) => setFixtures(r.data));
    client.get("/players?club=agama").then(() => {});
  }, []);
  const result = async (f, hs, as_) => { await client.post(`/staff/fixtures/${f.id}/result`, { home_score: +hs, away_score: +as_ }); toast.success("Result published"); };
  const saveStrip = async () => { try { await client.post("/staff/reserved-strip", { advertiser: strip }); toast.success("Reserved strip updated"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-6">
      <div className={box}>
        <div className={label + " mb-3"}>Official results</div>
        <div className="space-y-2">
          {fixtures.map((f) => <ResultRow key={f.id} f={f} onSave={result} />)}
        </div>
      </div>
      <StatsPackPanel />
      <div className={box}>
        <div className={label + " mb-2"}>Reserved league-sponsor strip</div>
        <div className="flex gap-2"><input value={strip} onChange={(e) => setStrip(e.target.value)} className={inp + " flex-1"} data-testid="strip-input" /><button onClick={saveStrip} className={btnG} data-testid="strip-save">Publish</button></div>
        <p className="text-xs text-ink/40 mt-2">Only PSL Staffer & Super Master Admin may edit this strip.</p>
      </div>
    </div>
  );
}
function ResultRow({ f, onSave }) {
  const [hs, setHs] = useState(f.home_score ?? ""); const [as_, setAs] = useState(f.away_score ?? "");
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={`staff-fixture-${f.id}`}>
      <span className="flex-1 text-right font-semibold truncate">{f.home_name}</span>
      <input value={hs} onChange={(e) => setHs(e.target.value)} className={inp + " w-12 text-center"} />
      <span>-</span>
      <input value={as_} onChange={(e) => setAs(e.target.value)} className={inp + " w-12 text-center"} />
      <span className="flex-1 font-semibold truncate">{f.away_name}</span>
      <button onClick={() => onSave(f, hs, as_)} className={btnG}>Save</button>
    </div>
  );
}
function StatsPackPanel() {
  const [sheets, setSheets] = useState([]);
  const load = () => client.get("/club/sheets").then(() => {}).catch(() => {});
  // staffer can't list club sheets by portal endpoint; use a lightweight fetch via fixtures->sheets is complex.
  return <div className={box}><div className={label}>Stats pack</div><p className="text-sm text-ink/60 mt-2">Attach the official statistical pack to a submitted sheet from the Match Centre after full-time. Media then confirm the XI (confirm is blocked until the pack is attached).</p></div>;
}

/* ---------------- Media / Newsroom ---------------- */
function MediaPanel({ user }) {
  if (user.desk === "photographer") return <PhotographerPanel />;
  if (user.desk === "editor_chief") return <EditorMediaPanel />;
  return <WriterPanel user={user} />;
}

function StatusPill({ status }) {
  const tone = status === "published" ? "green" : status === "pending" ? "gold" : "muted";
  return <Pill tone={tone}>{status || "—"}</Pill>;
}

function WriterPanel({ user }) {
  const [stories, setStories] = useState([]);
  const [form, setForm] = useState({ title: "", body: "" });
  const deskType = { news: "news", articles: "articles", updates: "updates" }[user.desk];
  const load = () => client.get("/editorial/mine").then((r) => setStories(r.data));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await client.post("/editorial/story", { type: deskType, ...form }); setForm({ title: "", body: "" }); load(); toast.success("Draft submitted to the Editor-in-Chief"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <div className={box}>
        <div className={label + " mb-2"}>Submit a /{deskType} draft — goes to the Editor-in-Chief for approval</div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Headline" className={inp + " w-full mb-2"} data-testid="story-title" />
        <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Body" rows={4} className={inp + " w-full mb-2"} data-testid="story-body" />
        <button onClick={submit} className={btnG} data-testid="publish-story">Submit for approval</button>
      </div>
      <div className={box}>
        <div className={label + " mb-2"}>My submissions</div>
        <div className="divide-y divide-pslborder">{stories.map((s) => <div key={s.id} className="py-2 text-sm font-semibold flex items-center gap-2">{s.title} <Pill tone="muted">{s.type}</Pill> <StatusPill status={s.status} /></div>)}</div>
      </div>
    </div>
  );
}

function EditorMediaPanel() {
  const [form, setForm] = useState({ type: "news", title: "", body: "" });
  const publish = async () => {
    try { await client.post("/editorial/story", form); setForm({ ...form, title: "", body: "" }); toast.success(`Published to /${form.type}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <div className={box}>
        <div className={label + " mb-2"}>Editor-in-Chief — publish directly</div>
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp + " mb-2 block"} data-testid="editor-type">
          {["news", "articles", "updates"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Headline" className={inp + " w-full mb-2"} data-testid="editor-title" />
        <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Body" rows={4} className={inp + " w-full mb-2"} data-testid="editor-body" />
        <button onClick={publish} className={btnG} data-testid="editor-publish">Publish now</button>
      </div>
      <EditorialPanel canRevoke={false} />
    </div>
  );
}

function PhotographerPanel() {
  const [url, setUrl] = useState("");
  const [lib, setLib] = useState([]);
  const load = () => client.get("/editorial/media-library").then((r) => setLib(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!url.trim()) return;
    try { await client.post("/editorial/media-library", { url }); setUrl(""); load(); toast.success("Added to media library"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <div className={box}>
        <div className={label + " mb-2"}>Photographer — add match imagery to the media library</div>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Image URL" className={inp + " w-full mb-2"} data-testid="media-url" />
        <button onClick={add} className={btnG} data-testid="add-media">Add image</button>
      </div>
      <div className={box}>
        <div className={label + " mb-2"}>Media library</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{lib.map((m) => <img key={m.id} src={m.url} alt="" className="w-full h-24 object-cover rounded-sm border border-pslborder" />)}</div>
      </div>
    </div>
  );
}

function EditorialPanel({ canRevoke }) {
  const [queue, setQueue] = useState([]);
  const [all, setAll] = useState([]);
  const load = () => {
    client.get("/editorial/queue").then((r) => setQueue(r.data)).catch(() => {});
    client.get("/admin/stories").then((r) => setAll(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const act = async (fn) => { try { await fn(); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-6">
      <div className={box} data-testid="editorial-queue">
        <div className={label + " mb-2"}>Approval queue ({queue.length})</div>
        {queue.length === 0 ? <p className="text-sm text-ink/50">No drafts awaiting approval.</p> : queue.map((s) => (
          <div key={s.id} className="py-2 border-b border-pslborder">
            <div className="font-semibold text-sm">{s.title} <Pill tone="muted">{s.type}</Pill> <span className="text-xs text-ink/40">by {s.author}</span></div>
            <p className="text-xs text-ink/60 line-clamp-2">{s.body}</p>
            <div className="flex gap-2 mt-1">
              <button onClick={() => act(() => client.post(`/editorial/story/${s.id}/approve`))} className={btnG} data-testid={`approve-${s.id}`}>Approve &amp; publish</button>
              <button onClick={() => act(() => client.post(`/editorial/story/${s.id}/reject`))} className="px-3 py-1.5 bg-white border border-pslborder text-sm font-bold rounded-sm" data-testid={`reject-${s.id}`}>Reject</button>
            </div>
          </div>
        ))}
      </div>
      <div className={box}>
        <div className={label + " mb-2"}>All content</div>
        <div className="divide-y divide-pslborder">
          {all.map((s) => (
            <div key={s.id} className="py-2 flex items-center gap-2 text-sm">
              <span className="font-semibold flex-1">{s.title}</span>
              <Pill tone="muted">{s.type}</Pill><StatusPill status={s.status} />
              {canRevoke && s.published && <button onClick={() => act(() => client.post(`/admin/story/${s.id}/revoke`))} className="text-xs text-live font-bold" data-testid={`revoke-${s.id}`}>Revoke</button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Sales ---------------- */
function SalesPanel({ user, superView }) {
  const [slots, setSlots] = useState([]);
  const load = () => client.get("/sales/slots").then((r) => setSlots(r.data));
  useEffect(() => { load(); }, []);
  const book = async (slot, { advertiser, category, price, file, status }) => {
    try {
      let creative = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file); fd.append("kind", "creative");
        const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        creative = data.path;
      }
      await client.post(`/sales/slots/${slot.slot_id}/flight`, { advertiser, category: category || null, price_note: price || null, creative, creative_type: creative ? "image" : "text", status });
      load(); toast.success(status === "flying" ? "Creative is now flying on the site" : "Slot booked");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className={box}>
      <div className={label + " mb-3"}>{superView ? "All ad slots (override)" : `My inventory · ${user?.section}`}</div>
      <p className="text-xs text-ink/50 mb-3">Upload creative, preview, then <b>Save &amp; fly</b> to publish it to the live pages. Pricing is set later in USD.</p>
      <div className="space-y-2">
        {slots.map((s) => <SlotRow key={s.slot_id} s={s} onBook={book} />)}
      </div>
    </div>
  );
}
function SlotRow({ s, onBook }) {
  const [adv, setAdv] = useState(s.advertiser || "");
  const [cat, setCat] = useState(s.category || "");
  const [price, setPrice] = useState(s.price_note || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(s.creative ? fileUrl(s.creative) : null);
  const reserved = s.reserved;
  const onFile = (e) => { const f = e.target.files?.[0]; if (!f) return; setFile(f); setPreview(URL.createObjectURL(f)); };
  return (
    <div className={`p-3 rounded-sm border ${reserved ? "bg-green/5 border-green/30" : "border-pslborder"}`} data-testid={`slot-${s.slot_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs font-bold text-green">{s.slot_id}</span>
        <Pill tone={s.status === "sold" || s.status === "flying" ? "green" : reserved ? "gold" : "muted"}>{s.status}</Pill>
        <span className="text-xs text-ink/40">{s.section} · {s.size}</span>
        {s.advertiser && <span className="text-xs text-ink/60">· {s.advertiser}</span>}
      </div>
      {reserved ? <p className="text-xs text-ink/50 mt-1">Reserved league-sponsor strip — not sellable.</p> : (
        <>
          <div className="flex gap-2 mt-2 flex-wrap items-center">
            <input value={adv} onChange={(e) => setAdv(e.target.value)} placeholder="Advertiser" className={inp + " flex-1 min-w-[120px]"} data-testid={`adv-${s.slot_id}`} />
            <input value={cat} onChange={(e) => setCat(e.target.value)} placeholder="Category" className={inp + " w-28"} data-testid={`cat-${s.slot_id}`} />
            <input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Price (USD)" className={inp + " w-28"} data-testid={`price-${s.slot_id}`} />
          </div>
          <div className="flex gap-2 mt-2 flex-wrap items-center">
            <label className="px-3 py-1.5 bg-cream border border-pslborder text-ink text-xs font-bold rounded-sm cursor-pointer hover:border-gold">
              {file ? "Change creative" : "Upload creative"}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`creative-${s.slot_id}`} />
            </label>
            {preview && <img src={preview} alt="creative preview" className="h-10 rounded-sm border border-pslborder object-contain bg-white" data-testid={`preview-${s.slot_id}`} />}
            <button onClick={() => onBook(s, { advertiser: adv, category: cat, price, file, status: "sold" })} className="px-3 py-1.5 bg-white border border-green text-green text-sm font-bold rounded-sm" data-testid={`book-${s.slot_id}`}>Mark sold</button>
            <button onClick={() => onBook(s, { advertiser: adv, category: cat, price, file, status: "flying" })} className={btnG} data-testid={`fly-${s.slot_id}`}>Save &amp; fly</button>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Club ---------------- */
function ClubPanel({ user }) {
  const [players, setPlayers] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [audit, setAudit] = useState([]);
  const [mw, setMw] = useState(2);
  const [xi, setXi] = useState([]);
  const [bench, setBench] = useState([]);
  const [club, setClub] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [support, setSupport] = useState({ message: "", goal_usd: "" });
  const load = () => {
    client.get(`/players?club=${user.club_slug}`).then((r) => setPlayers(r.data));
    client.get("/club/sheets").then((r) => setSheets(r.data));
    client.get("/club/sheets/audit").then((r) => setAudit(r.data));
    client.get(`/clubs/${user.club_slug}`).then((r) => setClub(r.data.club)).catch(() => {});
  };
  const onLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "badge");
      const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const abs = process.env.REACT_APP_BACKEND_URL + data.url;
      await client.post("/club/media", { kind: "badge", url: abs });
      toast.success("Club logo updated"); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const saveSupport = async () => {
    if (!support.message.trim()) { toast.error("Enter a support message"); return; }
    try { await client.post("/club/support", { message: support.message, goal_usd: support.goal_usd ? Number(support.goal_usd) : null, active: true }); toast.success("Diaspora support appeal published"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const toggle = (id, list, setList, max) => {
    if (list.includes(id)) setList(list.filter((x) => x !== id));
    else if (list.length < max) setList([...list, id]);
  };
  const submit = async () => {
    if (xi.length !== 11) { toast.error("Starting XI needs 11 players"); return; }
    try { await client.post("/club/sheets", { matchweek: +mw, starting_xi: xi, bench }); toast.success("Team sheet submitted"); setXi([]); setBench([]); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <div className={box}>
        <div className={label + " mb-3"}>Club identity · logo</div>
        <div className="flex items-center gap-4">
          <Crest slug={user.club_slug} name={club?.name} size={76} url={club?.badge} />
          <div>
            <div className="font-head font-bold text-green">{club?.name || user.club_slug}</div>
            <p className="text-xs text-ink/50 mb-2">Upload a new club logo (PNG / JPG / WebP). Displayed across the whole site.</p>
            <label className="inline-block px-4 py-2 bg-gold text-ink text-sm font-bold rounded-sm cursor-pointer hover:bg-green hover:text-cream transition-colors" data-testid="club-logo-upload">
              {uploading ? "Uploading…" : "Change logo"}
              <input type="file" accept="image/*" className="hidden" onChange={onLogo} disabled={uploading} />
            </label>
          </div>
        </div>
      </div>
      <div className={box}>
        <div className={label + " mb-2"}>Diaspora support appeal (fantasy page)</div>
        <p className="text-xs text-ink/50 mb-2">Publish an appeal so the diaspora can donate to your club. Platform retains the configured fee; your club keeps the rest.</p>
        <textarea value={support.message} onChange={(e) => setSupport({ ...support, message: e.target.value })} placeholder="Why you're raising support…" rows={2} className={inp + " w-full mb-2"} data-testid="support-message" />
        <div className="flex gap-2">
          <input type="number" value={support.goal_usd} onChange={(e) => setSupport({ ...support, goal_usd: e.target.value })} placeholder="Goal (USD, optional)" className={inp + " w-48"} data-testid="support-goal" />
          <button onClick={saveSupport} className={btnG} data-testid="save-support">Publish appeal</button>
        </div>
      </div>
      <div className={box}>
        <div className="flex items-center justify-between mb-3">
          <div className={label}>Submit team sheet · {user.club_slug}</div>
          <div className="flex items-center gap-2"><span className="text-xs">MW</span><input type="number" value={mw} onChange={(e) => setMw(e.target.value)} className={inp + " w-16"} /></div>
        </div>
        <p className="text-xs text-ink/50 mb-2">Select 11 for the XI ({xi.length}/11) and up to 7 bench ({bench.length}).</p>
        <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto scrollbar-thin">
          {players.map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-sm p-1.5 border border-pslborder rounded-sm" data-testid={`cp-${p.id}`}>
              <span className="w-6 text-center text-ink/40 tabular">{p.shirt}</span>
              <span className="flex-1 truncate">{p.name} <span className="text-ink/40 text-xs">{p.position}</span></span>
              <button onClick={() => toggle(p.id, xi, setXi, 11)} className={`text-xs px-2 py-0.5 rounded-sm font-bold ${xi.includes(p.id) ? "bg-green text-cream" : "bg-cream border border-pslborder"}`} data-testid={`xi-${p.id}`}>XI</button>
              <button onClick={() => toggle(p.id, bench, setBench, 7)} className={`text-xs px-2 py-0.5 rounded-sm font-bold ${bench.includes(p.id) ? "bg-gold text-ink" : "bg-cream border border-pslborder"}`}>Bench</button>
            </div>
          ))}
        </div>
        <button onClick={submit} className={btnG + " mt-3"} data-testid="submit-sheet-btn">Submit sheet</button>
      </div>
      <div className={box}>
        <div className={label + " mb-2"}>My sheets</div>
        {sheets.map((s) => <div key={s.id} className="py-2 text-sm border-b border-pslborder flex gap-2 items-center">MW{s.matchweek} <Pill tone={s.status === "confirmed" ? "confirmed" : "unconfirmed"}>{s.status}</Pill>{s.stats_pack && <Pill tone="muted">stats pack</Pill>}</div>)}
      </div>
      <div className={box}>
        <div className={label + " mb-2"}>Sheet audit log</div>
        <div className="max-h-64 overflow-y-auto scrollbar-thin divide-y divide-pslborder">
          {audit.map((a) => <div key={a.id} className="py-2 text-xs"><span className="text-ink/40">{new Date(a.ts).toLocaleString("en-GB")}</span> · {a.actor} · MW{a.matchweek} · in {a.players_in.length} / out {a.players_out.length}</div>)}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Fantasy scoring rules (super admins) ---------------- */
function ScoringPanel() {
  const [rules, setRules] = useState(null);
  useEffect(() => { client.get("/admin/fantasy/scoring").then((r) => setRules(r.data)); }, []);
  if (!rules) return <div className={box}>Loading…</div>;
  const fields = [
    ["appearance", "Appearance (played)"],
    ["goal_gk", "Goal — GK"], ["goal_def", "Goal — DEF"], ["goal_mid", "Goal — MID"], ["goal_fwd", "Goal — FWD"],
    ["assist", "Assist"],
    ["clean_sheet_gk", "Clean sheet — GK"], ["clean_sheet_def", "Clean sheet — DEF"], ["clean_sheet_mid", "Clean sheet — MID"], ["clean_sheet_fwd", "Clean sheet — FWD"],
  ];
  const save = async () => {
    try { const { data } = await client.post("/admin/fantasy/scoring", rules); toast.success(`Saved — ${data.players_updated} players, ${data.squads_updated} squads recomputed`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className={box} data-testid="scoring-panel">
      <div className={label + " mb-1"}>Fantasy player-points rules</div>
      <p className="text-xs text-ink/50 mb-3">Set how many points each action is worth. Saving recomputes every player and squad total.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {fields.map(([k, lbl]) => (
          <div key={k}>
            <div className="text-xs text-ink/60 mb-1">{lbl}</div>
            <input type="number" value={rules[k]} onChange={(e) => setRules({ ...rules, [k]: parseInt(e.target.value || "0", 10) })} className={inp + " w-full"} data-testid={`scoring-${k}`} />
          </div>
        ))}
      </div>
      <button onClick={save} className={btnG + " mt-4"} data-testid="save-scoring">Save &amp; recompute</button>
    </div>
  );
}

/* ---------------- Reports ---------------- */
function ReportsPanel() {
  const [rep, setRep] = useState(null);
  useEffect(() => { client.get("/admin/reports").then((r) => setRep(r.data)); }, []);
  if (!rep) return <div className={box}>Loading…</div>;
  const Stat = ({ label: l, value }) => <div className="p-3 rounded-sm border border-pslborder"><div className="text-[11px] uppercase tracking-wider text-ink/50">{l}</div><div className="font-head font-black text-xl text-green tabular">{value}</div></div>;
  return (
    <div className="space-y-4" data-testid="reports-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total users" value={rep.users.total} />
        <Stat label="Fantasy squads" value={rep.fantasy.squads} />
        <Stat label="Donations raised" value={`$${rep.donations.raised.toFixed(2)}`} />
        <Stat label="Platform fees" value={`$${rep.donations.platform_fee.toFixed(2)}`} />
      </div>
      <div className={box}>
        <div className={label + " mb-2"}>Users by role</div>
        <div className="flex flex-wrap gap-2 text-sm">{Object.entries(rep.users.by_role).map(([r, n]) => <Pill key={r} tone="muted">{r}: {n}</Pill>)}</div>
      </div>
      <div className={box}>
        <div className={label + " mb-2"}>Ad slots</div>
        <div className="flex flex-wrap gap-2 text-sm">{Object.entries(rep.ads.by_status).map(([s, n]) => <Pill key={s} tone="muted">{s}: {n}</Pill>)}</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className={box}><div className={label + " mb-2"}>Top players</div>{rep.top_players.map((p, i) => <div key={i} className="flex justify-between text-sm py-1 border-b border-pslborder"><span>{p.name} <span className="text-ink/40 text-xs">{p.club_slug}</span></span><b className="text-green tabular">{p.points}</b></div>)}</div>
        <div className={box}><div className={label + " mb-2"}>Top fantasy squads</div>{rep.top_squads.length === 0 ? <p className="text-sm text-ink/50">No squads yet.</p> : rep.top_squads.map((s, i) => <div key={i} className="flex justify-between text-sm py-1 border-b border-pslborder"><span>{s.team_name || s.username}</span><b className="text-green tabular">{s.points}</b></div>)}</div>
      </div>
    </div>
  );
}

/* ---------------- Feedback inbox ---------------- */
function FeedbackPanel() {
  const [rows, setRows] = useState([]);
  const load = () => client.get("/admin/feedback").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const resolve = async (f) => { try { await client.post(`/admin/feedback/${f.id}/resolve`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className={box} data-testid="feedback-panel">
      <div className={label + " mb-2"}>User feedback inbox</div>
      {rows.length === 0 ? <p className="text-sm text-ink/50">No feedback yet.</p> : (
        <div className="divide-y divide-pslborder">
          {rows.map((f) => (
            <div key={f.id} className="py-3" data-testid={`feedback-${f.id}`}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Pill tone="muted">{f.category}</Pill>
                <span className="font-semibold text-sm">{f.name}</span>
                {f.email && <span className="text-xs text-ink/50">{f.email}</span>}
                <span className="text-xs text-ink/40 ml-auto">{new Date(f.created_at).toLocaleString("en-GB")}</span>
              </div>
              <p className="text-sm text-ink/70">{f.message}</p>
              <button onClick={() => resolve(f)} className={`mt-1 text-xs font-bold ${f.resolved ? "text-ink/40" : "text-green"}`}>{f.resolved ? "Resolved ✓ (undo)" : "Mark resolved"}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- shared ---------------- */
function TabBar({ tabs, tab, setTab }) {
  return <div className="flex gap-2 mb-4 flex-wrap">{tabs.map(([k, v]) => <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`px-3 py-1.5 rounded-sm text-sm font-bold ${tab === k ? "bg-green text-cream" : "bg-white border border-pslborder"}`}>{v}</button>)}</div>;
}